import { get } from 'lodash'
import { rem, remToPx, stripUnit } from 'polished'
import React, { useRef } from 'react'
import { useMedia as useMediaHook } from 'react-use'
import styled from 'styled-components'
import { PUBLIC_LAYOUT_SIDE_PADDING } from '@public/components/PublicLayout/PublicLayout'
import { CardCarousel } from 'components/CardCarousel'
import { ARROW_WITH_BACKGROUND_WRAPPER_HEIGHT } from 'components/CarouselNavigation'
import { CollectionWidgetCard } from 'components/CollectionWidgetCard'
import { Box, CONTAINER_HORIZONTAL_PADDING, Flex } from 'components/Layout'
import { H5 } from 'components/Typography'
import { COMMISSION_TYPES } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import {
  GA4_EVENTS,
  GA4_ITEM_LIST_NAME,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useGAListItemView } from 'providers/googleAnalytics/utils/ListView/useGAListItemView'
import { DEVICE, mq, PAGE_MAX_WIDTH, SPACE } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'

const CARD_WIDTH = { MOBILE: 'auto', TABLET: rem(170) }
const CAROUSEL_VERTICAL_PADDING = SPACE.PX_16
const CAROUSEL_OFFSET = {
  MOBILE: parseInt(stripUnit(remToPx(CONTAINER_HORIZONTAL_PADDING)) as string),
  WIDE_MOBILE: parseInt(
    stripUnit(remToPx(PUBLIC_LAYOUT_SIDE_PADDING)) as string
  ),
  DESKTOP: 10,
}
const SLIDES_PER_VIEW = { MOBILE: 2.3, DESKTOP: 'auto' } as const
const WIDE_MOBILE_MEDIA_QUERY = `screen and (max-width: ${DEVICE.TABLET}) and (min-width: ${PAGE_MAX_WIDTH.PUBLIC_LAYOUT_MOBILE})`

const Wrapper = styled(Box)`
  margin: -${CAROUSEL_VERTICAL_PADDING} -${CONTAINER_HORIZONTAL_PADDING};
  @media ${WIDE_MOBILE_MEDIA_QUERY} {
    margin: -${CAROUSEL_VERTICAL_PADDING} -${PUBLIC_LAYOUT_SIDE_PADDING};
  }
  ${mq.from.TABLET`
    margin: -${CAROUSEL_VERTICAL_PADDING} 0;
`}
`

const Title = styled(H5)`
  margin: ${SPACE.PX_16} ${CONTAINER_HORIZONTAL_PADDING} 0
    ${CONTAINER_HORIZONTAL_PADDING};
  @media ${WIDE_MOBILE_MEDIA_QUERY} {
    margin-left: ${PUBLIC_LAYOUT_SIDE_PADDING};
    margin-right: ${PUBLIC_LAYOUT_SIDE_PADDING};
  }
  ${mq.from.TABLET`
    margin-left: 0;
    margin-right: 0;
`}
`

interface RecommendedProductsCarouselProps {
  recommendedProducts?: PublicProductQuery['product']['recommendedProducts']
  commissionedExpertSlug?: string
  isTitleVisible: boolean
}

const RecommendedProductsCarouselContent: React.FC<
  React.PWC<RecommendedProductsCarouselProps>
> = ({ isTitleVisible, recommendedProducts, commissionedExpertSlug }) => {
  const isWideMobile = useMediaHook(WIDE_MOBILE_MEDIA_QUERY, false)

  const intersectionRef = useRef(null)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  useGAListItemView({
    itemRef: intersectionRef,
    isActive: recommendedProducts?.length > 0,
    reportData: recommendedProducts.map(({ pairedProduct }, index) => ({
      ...pairedProduct.variants?.[0],
      index,
    })),
    onViewEvent: (items) => {
      executeDataToDataLayer({
        event: GA4_EVENTS.VIEW_ITEM_LIST,
        overrideData: {
          items,
        },
      })
    },
  })

  if (!recommendedProducts?.length) {
    return null
  }

  return (
    <Wrapper overflow="hidden" ref={intersectionRef} pt={SPACE.PX_32}>
      {isTitleVisible && <Title>Pairs well with</Title>}
      <Box>
        <CardCarousel
          py={CAROUSEL_VERTICAL_PADDING}
          slidesPerView={SLIDES_PER_VIEW}
          offset={{
            MOBILE: isWideMobile
              ? CAROUSEL_OFFSET.WIDE_MOBILE
              : CAROUSEL_OFFSET.MOBILE,
            DESKTOP: CAROUSEL_OFFSET.DESKTOP,
          }}
          cards={recommendedProducts.map(
            (
              {
                id,
                pairedProduct: {
                  title,
                  images,
                  slug,
                  variants,
                  brand,
                  availability,
                },
              },
              index
            ) => ({
              card: (
                <Flex height="100%" width={CARD_WIDTH}>
                  <CollectionWidgetCard
                    index={index}
                    title={title}
                    productImageUrl={get(images, [0, 'url'])}
                    productImageAlt={get(images, [0, 'alt'])}
                    variants={variants}
                    url={
                      commissionedExpertSlug
                        ? ROUTE.SHOWROOM_COLLECTION_PRODUCT_DETAIL({
                            slug: commissionedExpertSlug,
                            pid: slug,
                            type: COMMISSION_TYPES.EXPERT_RECOMMENDATION_CAROUSEL,
                          })
                        : ROUTE.SHOWROOM_PRODUCT_DETAIL({
                            slug,
                          })
                    }
                    brand={brand?.id}
                    availability={availability}
                    productId={id}
                  />
                </Flex>
              ),
              key: id,
            })
          )}
          arrowsOffsetTop={`calc(1rem + (${CARD_WIDTH.TABLET} / 2) - (${ARROW_WITH_BACKGROUND_WRAPPER_HEIGHT} / 2))`} // centers arrow according to image (1 rem in the beginning is top padding of swiper)
        />
      </Box>
    </Wrapper>
  )
}

export const RecommendedProductsCarousel = withGA4Provider({
  item_list_name: GA4_ITEM_LIST_NAME.EXPERT_RECOMMENDS,
})(RecommendedProductsCarouselContent)
